import { MoneyBackIcon } from "@xola/icons";
import {
    DateFormat,
    formatDate,
    getAuditRefundedAmount,
    getSubmittedCancellationProtectionClaim,
    isProcessedClaimStatus,
} from "common";
import dayjs from "dayjs";
import React from "react";
import { Audit, Purchase, PurchaseItem } from "types";
import { Currency } from "../../../Common/Currency";
import { TimelineItem, TimelineItemReason } from "../TimelineItem";

interface CancellationProtectionTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const CancellationProtectionTimelineItem = ({
    purchase,
    item,
    audit,
    showDate = false,
}: CancellationProtectionTimelineItemProps) => {
    const claim = getSubmittedCancellationProtectionClaim(item, audit);

    if (!claim) {
        return null;
    }

    const isProcessed = isProcessedClaimStatus(claim.status);
    const payoutDate = claim.processRefundAt
        ? formatDate(dayjs(claim.processRefundAt), DateFormat.DATE_TIME_SHORT)
        : "";
    const refundedAmount = getAuditRefundedAmount(item, audit);

    return (
        <TimelineItem
            title={isProcessed ? "Refund Processed" : "Cancellation Protection Claim Submitted"}
            audit={audit}
            icon={<MoneyBackIcon />}
            color="primary"
            showDate={showDate}
            purchase={purchase}
        >
            <div className="space-y-2" data-testid="cancellation-protection-timeline-item">
                {isProcessed ? (
                    <TimelineItemReason>
                        {refundedAmount === undefined ? (
                            "A refund has been processed."
                        ) : (
                            <>
                                A refund of{" "}
                                <Currency currency={purchase.currency} shouldRemoveTrailingZeroes={false}>
                                    {refundedAmount}
                                </Currency>{" "}
                                has been processed.
                            </>
                        )}
                    </TimelineItemReason>
                ) : (
                    <>
                        {claim.claimReason ? <TimelineItemReason>{claim.claimReason}</TimelineItemReason> : null}

                        {payoutDate ? (
                            <div className="text-sm text-gray-dark">
                                The traveler&apos;s refund is scheduled to be processed on {payoutDate}.
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </TimelineItem>
    );
};

CancellationProtectionTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) =>
    !!getSubmittedCancellationProtectionClaim(item, audit);
